// Browser polyfills
import 'whatwg-fetch';

// Libs
import React, { useCallback, useState } from 'react';
import { Layout, Result } from 'antd';
const { Content } = Layout;
import NackleMenu from '@nackle/menu';

// Routing
import { Route, Link, routeHistory } from '@nackle/biw-web-router';
import {
  routeList,
  bundleRoutingMiddleware,
  replacePatternVars,
  redirectMiddleware,
} from './app-routing.jsx';

// Bridge
import { Bridge } from '@nackle/bridge';
import auth, { hasPermission } from './auth/app-auth.js';

// Components
import {
  AppHeader,
  AppFooter,
  AppError,
  ActivityIndicator,
  applyRequestMiddleware,
  authRequestMiddleware,
  authRouteMiddleware,
} from '@nackle/studio-tools';

// Services
import { setLanguageBundle } from '@nackle/intl-tools';
import useCmxService from './hooks/useCmxService.jsx';

// Styles
import './styles/app.less';

/*******************************************************************************
 *  1. App (root level component)
 */

const App = () => {
  const [ cmxLoaded, setCmxLoaded ] = useState( false );
  const { getMergedBundle } = useCmxService( false );

  const handleSignIn = async ( state ) => {
    await routeHistory.refresh();

    const locale = state.claims?.locale;
    const bundle = await getMergedBundle( [
      'navigation',
      'studio-in-context-help',
    ] );

    await setLanguageBundle( locale, bundle );

    setCmxLoaded( true );
  };

  const handleCompanyChange = useCallback(
    ( route ) => ( company ) => {
      if ( route?.match?.pattern === '/' ) {
        routeHistory.push( `/${ company.id }` );
      } else if ( route?.match ) {
        routeHistory.push(
          replacePatternVars( route?.match?.pattern, {
            ...route?.state?.params,
            companyId: company.id,
          } )
        );
      } else {
        routeHistory.push( `/${ company.id }/search` );
      }
    },
    []
  );

  return (
    <AppError>
      <Route
        render={ ( route ) => {
          // set the title
          document.title = route && route.match ? route.match.title : 'Home';
          // get the companyId from the route
          const companyId = route.state?.params?.companyId;
          // permissions
          const permissionToView =
            route.match && hasPermission( route.match.permission );
          return (
            <Bridge auth={ auth } onSignIn={ handleSignIn }>
              { cmxLoaded ? (
                <Layout className="root-layout">
                  <AppHeader
                    companyId={ companyId }
                    auth={ auth }
                    onCompanyChange={ handleCompanyChange( route ) }
                  />
                  <Layout className="test-layout">
                    <NackleMenu auth={ auth } cmxBundleLoaded={ cmxLoaded } />
                    <Layout className="page-layout">
                      <Content className="app-content">
                        { route.match && permissionToView ? (
                          React.cloneElement( route.component, {
                            key: companyId,
														route,
														hasPermission
                          } )
                        ) : route.match && !permissionToView ? (
                          <Result
                            status="403"
                            title="Access Denied"
                            subTitle="Sorry, you do not have permissions for the page you visited."
                            extra={
                              <Link to="/" className="ant-btn ant-btn-primary">
                                Back Home
                              </Link>
                            }
                          />
                        ) : (
                          <Result
                            status="404"
                            title="404"
                            subTitle="Sorry, the page you visited does not exist."
                            extra={
                              <Link to="/" className="ant-btn ant-btn-primary">
                                Back Home
                              </Link>
                            }
                          />
                        ) }
                      </Content>
                      <AppFooter />
                    </Layout>
                  </Layout>
                </Layout>
              ) : (
                <ActivityIndicator />
              ) }
            </Bridge>
          );
        } }
      />
    </AppError>
  );
};

/*******************************************************************************
 *  2. Connect Routing
 */
routeHistory.applyRoutes( routeList );
routeHistory.applyMiddleware( [
  authRouteMiddleware( auth ),
  bundleRoutingMiddleware,
  ...redirectMiddleware,
] );

/*******************************************************************************
 *  3. Connect Auth to Fetch
 */
applyRequestMiddleware( authRequestMiddleware( auth ) );

/*******************************************************************************
 * 4. Export the app for rendering
 */

export default App;
